import React from "react";
import { Box, IconButton, styled, Typography, SxProps, Stack, LinearProgress, Grid } from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import { useTranslation } from "react-i18next";

export type ToastProps = {
  sx?: SxProps;
  className?: string;
  title?: string;
  body?: string | null;
  type?: "warning" | "success" | "error" | "primary";
  rightContent?: React.ReactNode;
  icon?: React.ReactNode;
  timer?: number;
  onClose?: () => void;
};

const Toast = React.forwardRef<any, ToastProps>((props, ref) => {
  const { t } = useTranslation();
  const {
    timer = 6000,
    title = t("component:toast.title.toastMessage"),
    body = null,
    type = "primary",
    icon,
    rightContent = null,
    onClose,
    ...otherProps
  } = props;
  let _icon;

  switch (type) {
    case "success":
      _icon = <CheckIcon />;
      break;
    case "primary":
      _icon = <InfoOutlinedIcon />;
      break;
    case "warning":
      _icon = <WarningAmberOutlinedIcon />;
      break;
    case "error":
      _icon = <ErrorOutlineOutlinedIcon />;
      break;
  }

  const displayedIcon = icon || _icon;
  const $timerInterval = React.useRef<NodeJS.Timer>(undefined);
  const [progress, setProgress] = React.useState(0);

  React.useEffect(() => {
    $timerInterval.current = setInterval(() => {
      setProgress((oldProgress) => {
        if (oldProgress === 100) {
          return 100;
        }
        const diff = oldProgress + 1;
        return diff;
      });
    }, timer / 100);

    return () => {
      clearInterval($timerInterval.current as any);

      if (progress === 100) onClose && onClose();
    };
  }, [$timerInterval, timer]);

  return (
    <StyledBox {...otherProps} ref={ref}>
      <StyledLinearProgress variant="determinate" value={progress} color={type} />
      <Grid container alignItems="center">
        <Grid item xs={1}>
          <CircleIcon alignItems="center" justifyContent="center" bgcolor={`${type}.main`}>
            {displayedIcon}
          </CircleIcon>
        </Grid>
        <Grid item xs={9}>
          <Stack>
            <Typography fontWeight={500} sx={{ overflowWrap: "break-word" }}>
              {title}
            </Typography>
            {body && (
              <Typography variant="body2" color="text.tier" sx={{ overflowWrap: "break-word" }}>
                {body}
              </Typography>
            )}
          </Stack>
        </Grid>
        <Grid item xs={1}>
          {rightContent}
        </Grid>
        <Grid item xs={1}>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Grid>
      </Grid>
    </StyledBox>
  );
});

export default Toast;

const StyledBox = styled(Box)(({ theme }) => ({
  position: "relative",
  backgroundColor: theme.palette.background.paper,
  borderRadius: 8,
  overflow: "hidden",
  padding: theme.spacing(2),
  width: 500,
}));

const StyledLinearProgress = styled(LinearProgress)(({ theme }) => ({
  position: "absolute",
  left: 0,
  top: 0,
  right: 0,
  background: "transparent",
}));

const CircleIcon = styled(Stack)(({ theme }) => ({
  width: 28,
  height: 28,
  borderRadius: 28,
  ".MuiSvgIcon-root": {
    fontSize: 16,
    color: theme.palette.background.paper,
  },
}));
